import { api } from "./api";

/**
 * Konvèti VAPID public key la an Uint8Array
 * pou PushManager ka itilize li.
 */
function urlBase64ToUint8Array(base64String) {
    if (!base64String) {
        throw new Error("VAPID public key la vid.");
    }

    const padding = "=".repeat(
        (4 - (base64String.length % 4)) % 4,
    );

    const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let index = 0; index < rawData.length; index += 1) {
        outputArray[index] = rawData.charCodeAt(index);
    }

    return outputArray;
}

/**
 * Konpare VAPID key ki nan subscription aktyèl la
 * ak nouvo VAPID key backend la voye.
 */
function applicationServerKeyMatches(subscription, publicKey) {
    const currentKey = subscription?.options?.applicationServerKey;

    if (!currentKey) {
        return false;
    }

    const expectedKey = urlBase64ToUint8Array(publicKey);
    const currentKeyBytes = new Uint8Array(currentKey);

    if (currentKeyBytes.length !== expectedKey.length) {
        return false;
    }

    return currentKeyBytes.every(
        (value, index) => value === expectedKey[index],
    );
}

/**
 * Anrejistre Service Worker la epi tann li pare.
 */
async function getServiceWorkerRegistration() {
    const existingRegistration =
        await navigator.serviceWorker.getRegistration("/");

    if (existingRegistration) {
        await existingRegistration.update().catch(() => null);
        return existingRegistration;
    }

    return navigator.serviceWorker.register("/sw.js", {
        scope: "/",
    });
}

/**
 * Voye subscription kliyan an bay backend la.
 */
async function saveSubscription(subscription, apiClient = api) {
    const subscriptionJson = subscription.toJSON();

    const p256dh = subscriptionJson?.keys?.p256dh;
    const auth = subscriptionJson?.keys?.auth;

    if (!subscription.endpoint || !p256dh || !auth) {
        throw new Error(
            "Push subscription lan pa gen tout enfòmasyon obligatwa yo.",
        );
    }

    await apiClient.post("/push/subscribe", {
        endpoint: subscription.endpoint,
        p256dh,
        auth,
    });
}

/**
 * Aktive push notifications pou kliyan ki konekte a.
 *
 * requestPermission:
 * - true: montre popup browser la pou mande pèmisyon.
 * - false: pa montre popup la otomatikman.
 */
export async function registerPush({
    requestPermission = true,
    apiClient = api,
} = {}) {
    if (
        !("serviceWorker" in navigator) ||
        !("PushManager" in window) ||
        !("Notification" in window)
    ) {
        console.warn(
            "Browser sa a pa sipòte Web Push Notifications.",
        );

        return false;
    }

    try {
        const registration =
            await getServiceWorkerRegistration();

        await navigator.serviceWorker.ready;

        if (Notification.permission === "default") {
            if (!requestPermission) {
                return false;
            }

            const permission =
                await Notification.requestPermission();

            if (permission !== "granted") {
                return false;
            }
        }

        if (Notification.permission !== "granted") {
            return false;
        }

        const response = await apiClient.get("/push/vapid-key");
        const publicKey = response?.data?.publicKey;

        if (!publicKey) {
            console.warn(
                "Backend la pa retounen VAPID public key la.",
            );

            return false;
        }

        let subscription =
            await registration.pushManager.getSubscription();

        /**
         * Si ansyen subscription lan te kreye ak yon lòt
         * VAPID key, efase li epi kreye yon nouvo.
         */
        if (
            subscription &&
            !applicationServerKeyMatches(
                subscription,
                publicKey,
            )
        ) {
            await subscription.unsubscribe().catch(() => false);
            subscription = null;
        }

        if (!subscription) {
            subscription =
                await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey:
                        urlBase64ToUint8Array(publicKey),
                });
        }

        await saveSubscription(subscription, apiClient);

        return true;
    } catch (error) {
        console.error(
            "Push notification registration failed:",
            error,
        );

        return false;
    }
}

/**
 * Dezaktive push notifications sou aparèy sa a.
 *
 * Fonksyon sa a ap eseye enfòme backend la tou.
 */
export async function unregisterPush() {
    if (!("serviceWorker" in navigator)) {
        return false;
    }

    try {
        const registration =
            await navigator.serviceWorker.getRegistration("/");

        if (!registration) {
            return true;
        }

        const subscription =
            await registration.pushManager.getSubscription();

        if (!subscription) {
            return true;
        }

        const endpoint = subscription.endpoint;

        try {
            await api.post("/push/unsubscribe", {
                endpoint,
            });
        } catch (error) {
            console.warn(
                "Backend unsubscribe endpoint la pa disponib:",
                error,
            );
        }

        await subscription.unsubscribe();

        return true;
    } catch (error) {
        console.error(
            "Push notification unregistration failed:",
            error,
        );

        return false;
    }
}

/**
 * Verifye si aparèy la deja gen yon push subscription.
 */
export async function hasPushSubscription() {
    if (
        !("serviceWorker" in navigator) ||
        Notification.permission !== "granted"
    ) {
        return false;
    }

    try {
        const registration =
            await navigator.serviceWorker.getRegistration("/");

        if (!registration) {
            return false;
        }

        const subscription =
            await registration.pushManager.getSubscription();

        return Boolean(subscription);
    } catch (_) {
        return false;
    }
}

/**
 * Son kout pou notifikasyon WebSocket pandan app la ouvri.
 *
 * Browser la ka bloke son an si itilizatè a poko
 * klike oswa kominike avèk paj la.
 */
let audioContext = null;

export function playNotifSound() {
    try {
        const AudioContextClass =
            window.AudioContext ||
            window.webkitAudioContext;

        if (!AudioContextClass) {
            return;
        }

        if (!audioContext) {
            audioContext = new AudioContextClass();
        }

        if (audioContext.state === "suspended") {
            audioContext.resume().catch(() => null);
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(
            880,
            audioContext.currentTime,
        );

        gainNode.gain.setValueAtTime(
            0.001,
            audioContext.currentTime,
        );

        gainNode.gain.exponentialRampToValueAtTime(
            0.18,
            audioContext.currentTime + 0.02,
        );

        gainNode.gain.exponentialRampToValueAtTime(
            0.0001,
            audioContext.currentTime + 0.45,
        );

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
    } catch (_) {
        // Pa bloke aplikasyon an si browser la refize son an.
    }
}